import type {
  DropDownRef,
  DropDownResult,
  JsonData,
  JsonDataItem,
  JsonDataWrapper,
  OdinDataRetrievalOptions,
} from '@myosh/odin-components';
import {
  DataSearchLocation,
  DropDownMultiSelect,
  DynamicFormRequiredType,
  OdinDataSender,
} from '@myosh/odin-components';
import { isArray } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AdvancedHierarchyTypeConfig, HierarchyDropdownValue } from '../../../@types/hierarchy-fields';
import { useCreateHierarchyValueMutation, useLazyHierarchyValuesQuery } from '../../../redux/services/hierarchy';
import { showError } from '../../../services/notification.service';
import { forceAssert } from '../../../utils/type-guards';
import { filterByParentId } from '../../../utils/webview-field-utils';
import { isWebView } from '../../../redux/util/webview-base-query';
import OdinDropDown from '../custom-drop-downs/odin-drop-down.component';
import type { HierarchyFieldGroupProps, RecordHierarchyFields } from './hierarchy-field-group.component';
import { hierarchyToDropDown, isHierarchyMandatory } from './hierarchy-functions';

interface HierarchyFieldProps extends Omit<HierarchyFieldGroupProps, 'onChange'> {
  hierarchyField: RecordHierarchyFields;
  isAdvancedHierarchiesEnabled?: boolean;
  advancedHierarchiesConfig?: AdvancedHierarchyTypeConfig;
  onChange: (
    hierarchyField: RecordHierarchyFields,
    fieldValue: HierarchyDropdownValue | HierarchyDropdownValue[]
  ) => void;
}

// The hierarchy field specific blank value
export const hierarchyDeselectValue: DropDownResult = {
  value: '',
  text: '',
  originalData: undefined,
};

const HierarchyField = ({
  hierarchyField,
  readOnly,
  value,
  onChange,
  errorObject,
  isUserHierarchy,
  isCompetencyHierarchy,
  isAdvancedHierarchiesEnabled,
  advancedHierarchiesConfig,
  usesMultiSelectDropdowns,
  showAddNewOption,
}: HierarchyFieldProps) => {
  const key = useRef(hierarchyField.caption);
  const subscriberRef = useRef<OdinDataSender<JsonDataWrapper> | undefined>(undefined);
  const dropDownRef = useRef<DropDownRef>(null);
  const parentId = useRef<number | undefined>(undefined);
  const isHierarchyFieldMandatory = isHierarchyMandatory(hierarchyField, isUserHierarchy, isCompetencyHierarchy);

  const { t } = useTranslation();

  const [createHierarchyValue] = useCreateHierarchyValueMutation();

  const [options, setOptions] = useState<OdinDataRetrievalOptions>();
  const [dropDownValue, setDropDownValue] = useState<DropDownResult | DropDownResult[]>();

  const [getHierarchyValues] = useLazyHierarchyValuesQuery();

  useEffect(() => {
    if (hierarchyField.id && options) {
      if (
        hierarchyField.dependsOnParent &&
        !parentId.current &&
        !usesMultiSelectDropdowns &&
        !(isAdvancedHierarchiesEnabled && advancedHierarchiesConfig?.showAll)
      ) {
        subscriberRef.current?.sendData();
      } else {
        const showAll = isAdvancedHierarchiesEnabled && advancedHierarchiesConfig?.showAll;
        const upperLimit = isAdvancedHierarchiesEnabled && advancedHierarchiesConfig?.upperBoundHierarchyTypeId;
        const inWebView = isWebView();
        getHierarchyValues(
          {
            page: inWebView ? 1 : (options.page ?? 1),
            pageSize: showAll || inWebView ? -1 : (options.pageSize ?? 50),
            sort: 'caption',
            filters: {
              ...(!upperLimit ? { typeId: { value: hierarchyField.id, comparison: '=' } } : {}),
              // In WebView mode, fetch all values and filter locally by parentId
              ...(parentId.current && !showAll && !inWebView
                ? { parentId: { value: parentId.current, comparison: '=' } }
                : {}),
              archived: { value: false, comparison: '=' },
              ...options.fieldFilters,
            },
          },
          true
        )
          .unwrap()
          .then((result) => {
            if (result?.items) {
              // In WebView mode, filter by parentId locally BEFORE mapping to avoid
              // depending on hierarchyToDropDown preserving parentId on every item
              const items =
                inWebView && parentId.current ? filterByParentId(result.items, parentId.current) : result.items;
              const dataItems = hierarchyToDropDown(items);

              let upperLimitValue;
              if (upperLimit) {
                for (const key in value) {
                  const item = value[key] as HierarchyDropdownValue;
                  if (item && item?.hierarchyTypeId === upperLimit) {
                    upperLimitValue = item;
                  }
                }

                const upperLimitOptions = getOptionsBasedOnUpperLimit(dataItems, upperLimitValue?.id ?? 0);
                subscriberRef.current?.sendData({
                  data: forceAssert<JsonData>(upperLimitOptions),
                  requestId: options.requestId,
                });
              } else {
                subscriberRef.current?.sendData({
                  data: forceAssert<JsonData>(dataItems),
                  requestId: options.requestId,
                });
              }

              if (result.page === result.availablePages) {
                subscriberRef.current?.sendData();
              }
            } else {
              subscriberRef.current?.sendData();
            }
          })
          .catch(() => subscriberRef.current?.sendData());
      }
    }
  }, [hierarchyField?.id, hierarchyField?.dependsOnParent, options]);

  // Returns available options based on the hierarchy type set as upper limit, if a value is selected for that field
  const getOptionsBasedOnUpperLimit = (items: HierarchyDropdownValue[], upperLimit: number) => {
    if (!upperLimit) {
      return items.filter((item) => item.hierarchyTypeId === hierarchyField.id);
    }

    let currentLevel = items.filter((item) => item.parentId === upperLimit);
    let lastNonEmpty = currentLevel;

    while (currentLevel.length > 0) {
      const nextLevel = items.filter((item) => currentLevel.some((currentItem) => item.parentId === currentItem.id));

      if (nextLevel.length > 0) {
        lastNonEmpty = nextLevel;
      }

      currentLevel = nextLevel;
    }

    return lastNonEmpty.filter((item) => item.hierarchyTypeId === hierarchyField.id);
  };

  useEffect(() => {
    if (value) {
      const currentHierarchyValue = value[key.current] ?? value.ignoreHierarchyAccess;

      if (hierarchyField.dependsOnParent && !isArray(currentHierarchyValue) && !usesMultiSelectDropdowns) {
        parentId.current = currentHierarchyValue?.selectedParentId;
        if (
          currentHierarchyValue.parentId === currentHierarchyValue?.selectedParentId &&
          Object.hasOwn(currentHierarchyValue, 'parentId')
        ) {
          setDropDownValue({
            value: currentHierarchyValue.id,
            text: currentHierarchyValue.caption,
            originalData: currentHierarchyValue,
          });
        } else {
          setDropDownValue(hierarchyDeselectValue);
          dropDownRef.current?.resetDropDownData();
          dropDownRef.current?.getDataOverwritePageCache();
        }
      } else if (!isArray(currentHierarchyValue) && !usesMultiSelectDropdowns) {
        setDropDownValue({
          value: currentHierarchyValue.id,
          text: currentHierarchyValue.caption,
          originalData: currentHierarchyValue,
        });
      } else if (isArray(currentHierarchyValue) && usesMultiSelectDropdowns) {
        const dropdownValues: DropDownResult[] = currentHierarchyValue.map((item) => {
          return {
            value: item.id,
            text: item.caption,
            originalData: item,
          };
        });
        setDropDownValue(dropdownValues);
      }
    }
  }, [value]);

  const getOptionsData = useMemo(() => {
    return {
      getData: async (subscriber: OdinDataSender<JsonDataWrapper>, options?: OdinDataRetrievalOptions) => {
        if (options?.page || options?.customProperties?.next !== undefined || options?.getAllData) {
          setOptions(options);
          subscriberRef.current = subscriber;
        } else {
          subscriber.sendData();
        }
      },
      saveData: (
        data: JsonDataItem | JsonData,
        returnResult: OdinDataSender<JsonDataWrapper>,
        options?: OdinDataRetrievalOptions
      ) => {
        if ('caption' in data && options?.customFieldType === 'ADDNEWMODAL') {
          createHierarchyValue({
            hierarchyTypeId: hierarchyField.id,
            caption: data.caption as string,
            latitude: 0,
            longitude: 0,
            archived: false,
          })
            .unwrap()
            .then((response) => {
              if (!Array.isArray(data) && response && 'result' in response) {
                const returnObj = {
                  caption: data.caption,
                  id: response.result,
                  hierarchyTypeId: hierarchyField.id,
                };
                returnResult.sendData({
                  data: [returnObj],
                  requestId: options.requestId,
                });
              } else if (Array.isArray(data)) {
                returnResult.sendData({ data, requestId: options.requestId });
              }
            })
            .catch((error) => {
              const errorMessage = error?.data?.validation?.responseErrors[0]?.message;
              const detailedMessage = error?.data?.validation?.errors[0] ?? '';
              showError(errorMessage ?? t('add-new-option-failed', { message: detailedMessage }));
              //Let component know there was an error
              returnResult.sendData({ customProperties: { isError: true, error: detailedMessage } });
            });
        }
      },
    };
  }, []);

  const onValueChange = (fieldValue: DropDownResult | DropDownResult[]) => {
    if (onChange && !!fieldValue) {
      const fieldOriginalData = isArray(fieldValue)
        ? (fieldValue.map((item) => {
            return item.originalData;
          }) as HierarchyDropdownValue[])
        : (fieldValue.originalData as HierarchyDropdownValue);
      onChange(hierarchyField, fieldOriginalData);
    }
  };

  const isEditable = readOnly || (isAdvancedHierarchiesEnabled && !advancedHierarchiesConfig?.editable);

  return (
    <div key={key.current} className={'mb-3'}>
      {usesMultiSelectDropdowns ? (
        <DropDownMultiSelect
          ref={dropDownRef}
          key={`dd_${key.current}`}
          textField="caption"
          valueField="id"
          value={dropDownValue}
          error={errorObject?.[key.current]?.id?.message}
          label={key.current}
          data={getOptionsData}
          readOnly={readOnly}
          onChange={(value) => onValueChange(value as DropDownResult[])}
          filterLocation={DataSearchLocation.Api}
        />
      ) : (
        <OdinDropDown
          ref={dropDownRef}
          key={`dd_${key.current}`}
          textField="caption"
          valueField="id"
          value={dropDownValue}
          error={errorObject?.[key.current]?.id?.message}
          label={key.current}
          data={getOptionsData}
          readOnly={isEditable}
          required={isHierarchyFieldMandatory ? DynamicFormRequiredType.True : DynamicFormRequiredType.False}
          onChange={(value) => onValueChange(value as DropDownResult)}
          deselectValue={hierarchyDeselectValue}
          allowClear={!isHierarchyFieldMandatory && Boolean((dropDownValue as DropDownResult)?.value)}
          filterLocation={DataSearchLocation.Api}
          showAddNewOption={showAddNewOption}
        />
      )}
    </div>
  );
};

export default HierarchyField;
